import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Input } from 'antd';
import { composeEvent, composeEventAsync } from '@icp/utils';

const NoI18nInput = forwardRef(function NoI18nInput(props, ref) {
  const {
    value: valueProp,
    onChange,
    onBlur,
    onKeyDown,
    onKeyUp,
    ...other
    //
  } = props;

  const [value, setValue] = useState(valueProp);
  const inputRef = useRef();
  // 让 keydown 和 keyup 之间 onChange 只会触发一次，防止一致按住 Enter 键反复触发 onChange
  const once = useRef(false);

  useImperativeHandle(
    ref,
    () => {
      if (inputRef.current) {
        inputRef.current[Symbol.for('icp_setValue')] = setValue;
      }
      return inputRef.current;
    },
    [],
  );

  // 当 changeDelayToBlur 时，按回车键也需要触发 onChange。和原生 input 逻辑一致
  const handleKeyDown = (event) => {
    if (once.current) {
      return;
    }

    if (event.key === 'Enter') {
      once.current = true;
      onChange(value);
    }
  };

  const handleKeyUp = (event) => {
    if (event.key === 'Enter') {
      once.current = false;
    }
  };

  return (
    <Input
      {...other}
      value={value ?? ''}
      onChange={(event) => {
        setValue(event.target.value);
        // 当前 focus 元素不是 input 同时他还触发了 onchange, 表示可能是浏览器 autocomplete 或者其余的非用户主动输入的情况
        if (document.activeElement !== inputRef.current.input) {
          onChange(event.target.value);
        }
      }}
      onBlur={composeEventAsync(() => onChange(value), onBlur)}
      onKeyDown={composeEvent(handleKeyDown, onKeyDown)}
      onKeyUp={composeEvent(handleKeyUp, onKeyUp)}
      ref={inputRef}
    />
  );
});

NoI18nInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
};

export default NoI18nInput;
